import type { Session } from '@supabase/supabase-js'

/**
 * The claims the Custom Access Token Hook emits (spec §5).
 *
 * These are read from the access token, never from a table and never from
 * client state. `org_id` in particular exists so the client can *render* the
 * right thing -- it is not what scopes the query. Postgres does that, from the
 * same token, via core.current_org_id(). If these two ever disagree, the
 * database is right.
 */
export type UserType = 'staff' | 'partner' | 'investor' | 'board' | null

export interface ExcellenceClaims {
  sub: string
  role: 'authenticated' | 'partner_user' | 'investor_user' | 'board_user' | 'anon'
  user_type: UserType
  org_id?: string
  partner_id?: string
  /** Org-level role: 'admin' can administer Excellence itself. */
  functional_role?: string
  /** core.staff_members.id — the key every business row hangs off, not auth.users.id. */
  staff_id?: string
  /** Module-scoped roles, e.g. { crm: ['sales_rep'], hr: ['hr_admin','employee'] }. */
  roles?: Record<string, string[]>
  partner_role?: string
  email?: string
  /** Investor only: when the data-room window closes. UI only -- the views read the table. */
  investor_expires_at?: string
  /**
   * Board member only (D77, BOARD.md §2.8): when the login's clock ends, absent
   * while any open seat is open-ended. UI only -- every `board_api` view reads
   * `core.board_users.access_until` itself.
   */
  board_access_until?: string
  /** GoTrue's session id, which an investor's usage events are grouped by. */
  session_id?: string
  /**
   * Set by `provision-investor` when the login is created. Read for ONE thing:
   * telling an investor whose window has closed (the hook now issues `anon`)
   * that it has closed, instead of showing them the staff sign-in. It grants
   * nothing -- a user can edit their own metadata, and all it can buy them is
   * a different refusal screen.
   */
  user_metadata?: { user_type?: string; full_name?: string } & Record<string, unknown>
}

/**
 * UI gating only.
 *
 * Authorization is enforced in Postgres by core.has_module_role(), which reads
 * the table rather than the token — so revoking a role takes effect on the next
 * query, not the next token refresh. A claim that has gone stale can render the
 * wrong nav item; it can never make the database permissive.
 */
export function hasModuleRole(
  claims: ExcellenceClaims | null,
  module: string,
  ...roles: string[]
): boolean {
  if (!claims) return false
  if (claims.functional_role === 'admin') return true
  const held = claims.roles?.[module] ?? []
  return roles.some((r) => held.includes(r))
}

/**
 * "Can this person open this module at all" — any role in it, whichever one.
 * Used to gate sidebar visibility and route entry (App.tsx, AppShell.tsx), so
 * it deliberately does not enumerate `core.module_roles` role names the way
 * `hasModuleRole` does: a new role added to a module (e.g. crm gains
 * 'sales_head') must not require a matching edit here to keep granting entry.
 */
export function hasAnyModuleRole(claims: ExcellenceClaims | null, module: string): boolean {
  if (!claims) return false
  if (claims.functional_role === 'admin') return true
  return (claims.roles?.[module]?.length ?? 0) > 0
}

/** Decode without verifying. Verification is the database's job, not the UI's. */
export function readClaims(session: Session | null): ExcellenceClaims | null {
  if (!session?.access_token) return null
  const payload = session.access_token.split('.')[1]
  if (!payload) return null
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decodeURIComponent(escape(json))) as ExcellenceClaims
  } catch {
    return null
  }
}

/**
 * An account that authenticated against Entra but is not in core.staff_members
 * gets role=anon and no org_id (§5 default deny). That is a real state, not an
 * error -- someone joined the directory before HR provisioned them -- so the app
 * has to render it rather than loop on the login screen.
 */
export function isProvisionedStaff(claims: ExcellenceClaims | null): boolean {
  return claims?.user_type === 'staff' && Boolean(claims.org_id)
}

/** A live data-room session (D44): the hook issued `investor_user`. */
export function isInvestor(claims: ExcellenceClaims | null): boolean {
  return claims?.user_type === 'investor' && claims.role === 'investor_user' && Boolean(claims.org_id)
}

/**
 * An investor login whose window has closed: the hook default-denied it to
 * `anon`, and only the metadata `provision-investor` wrote still says what it
 * was. Rendering only -- see `user_metadata` above.
 */
export function wasInvestor(claims: ExcellenceClaims | null): boolean {
  return !claims?.user_type && claims?.user_metadata?.user_type === 'investor'
}

/** A live board-member session (D77, BOARD.md §2.8): the hook issued `board_user`. */
export function isBoard(claims: ExcellenceClaims | null): boolean {
  return claims?.user_type === 'board' && claims.role === 'board_user' && Boolean(claims.org_id)
}

/**
 * A board login whose last seat has ended: the hook default-denied it to
 * `anon`, and only the metadata `provision-board-member` wrote still says what
 * it was. Rendering only -- see `user_metadata` above.
 */
export function wasBoard(claims: ExcellenceClaims | null): boolean {
  return !claims?.user_type && claims?.user_metadata?.user_type === 'board'
}
